import { inject, Injectable, Injector, resource, signal } from '@angular/core';
import { MachineCatalogRepository } from '../repositories/machine-catalog.repository';
import { MetaFormBody } from '../types/meta-form-body';
import { handleRequestError } from '@softline/application';
import { buildMachineQueryPayload } from '../utils/build-machine-query-payload';
import { saveAs } from '@softline/core';

@Injectable()
export class MachineCatalogService {
  readonly #machineCatalogRepo = inject(MachineCatalogRepository);
  readonly #injector = inject(Injector);

  readonly showPrices = signal(true);

  readonly page = signal(0);
  readonly PAGE_SIZE = 10;

  readonly machineId = signal<undefined | number | string>(undefined);

  readonly metaFormBody = signal<MetaFormBody | null>(null);

  readonly additionalFilters = signal<any | null>(null);

  readonly machines = resource({
    params: () => ({
      additionalFilters: this.additionalFilters(),
      metaFormBody: this.metaFormBody(),
      page: this.page(),
    }),
    loader: async ({ params }) => {
      const payload = buildMachineQueryPayload(
        params.additionalFilters,
        params.metaFormBody,
        this.PAGE_SIZE,
        params.page * this.PAGE_SIZE
      );
      try {
        return await this.#machineCatalogRepo.additionalFilter(payload);
      } catch (err) {
        handleRequestError(err, this.#injector, { show: true });
        throw err;
      }
    },
  });

  readonly machineData = resource({
    params: () => ({ machineId: this.machineId() }),
    loader: async (param) => {
      if (!param.params.machineId) return undefined;
      try {
        return await this.#machineCatalogRepo.machineById(param.params.machineId);
      } catch (err) {
        handleRequestError(err, this.#injector, { show: true });
        throw err;
      }
    },
  });

  readonly machineMetaForm = resource({
    params: () => ({ metaFormBody: this.metaFormBody() }),
    loader: async (param) => {
      const payload = !param.params.metaFormBody
        ? ({} as MetaFormBody)
        : param.params.metaFormBody;
      try {
        return await this.#machineCatalogRepo.metaForm(payload);
      } catch (err) {
        handleRequestError(err, this.#injector, { show: true });
        throw err;
      }
    },
  });

  readonly machineNotes = resource({
    params: () => ({ machineId: this.machineId() }),
    loader: async (param) => {
      if (!param.params.machineId) return undefined;
      try {
        return await this.#machineCatalogRepo.machineNotes(param.params.machineId);
      } catch (err) {
        handleRequestError(err, this.#injector, { show: true });
        throw err;
      }
    },
  });

  readonly machineImages = resource({
    params: () => ({
      machineId: this.machineId(),
      imgPaths: this.machineData.value()?.images,
    }),
    loader: async ({ params }) => {
      const { machineId, imgPaths } = params;
      if (!machineId || !imgPaths) return null;
      try {
        const results = await Promise.all(
          imgPaths.map((path: string) =>
            this.#machineCatalogRepo.machineImageByPath(machineId, path)
          )
        );
        return results.filter((url): url is string => url !== null);
      } catch (err) {
        handleRequestError(err, this.#injector, { show: true });
        throw err;
      }
    },
  });

  readonly machineCover = resource({
    params: () => ({
      machineId: this.machineId(),
      coverPath: this.machineData.value()?.cover,
    }),
    loader: async ({ params }) => {
      const { machineId, coverPath } = params;
      if (!machineId || !coverPath) return null;
      try {
        return await this.#machineCatalogRepo.machineImageByPath(machineId, coverPath);
      } catch (err) {
        handleRequestError(err, this.#injector, { show: true });
        throw err;
      }
    },
  });

  async createMachineNote(machineId: number | string, note: string): Promise<any> {
    return await this.#machineCatalogRepo.createMachineNote(machineId, note);
  }

  async downloadMachineDataSheet(machineId: number | string): Promise<void> {
    const blob = await this.#machineCatalogRepo.machineDataSheet(machineId);
    if (blob) {
      saveAs(blob, `Datenblatt_Maschine_${machineId}.pdf`);
    }
  }
}
